import Joi from 'joi';
import { IANAZone } from 'luxon';
import { isProxy, toRaw } from 'vue';
import moment from 'moment-timezone';
import { InteractionSummaryTypes } from '../../types';

// Copied the joi-tz library as it didn't have typings, and this was easier than creating types for it
// We can probably do all this timezone stuff from native browser
const JoiTZ = Joi.extend({
    type: 'timezone',
    base: Joi.string(),
    messages: { timezone: '"{{#label}}" must be at least a valid timezone' },
    rules: {},
    validate(value, helpers) {
        if (!IANAZone.isValidZone(value)) {
            return { value, errors: helpers.error('timezone') };
        }
        return { value };
    }
});
const JoiCsv = Joi.extend((joi) => ({
    base: joi.array(),
    coerce: (value) => ({ value: value.split ? value.split(',') : value }),
    type: 'csvArray'
}));

const getInteractionsSchema = Joi.object().keys({
    fromDate: Joi.string().isoDate().required(),
    toDate: Joi.string().isoDate().required(),
    teamIds: JoiCsv.csvArray(),
    userIds: JoiCsv.csvArray(),
    workstreams: JoiCsv.csvArray(),
    roleIds: JoiCsv.csvArray(),
    tags: Joi.string(),
    timezone: JoiTZ.timezone()
});

export const validateInteractionQuery = (params: InteractionSummaryTypes.InteractionSummaryFilter): Joi.ValidationResult => {
    return getInteractionsSchema.validate(params);
};

// Actually not too bad but scored 27
// eslint-disable-next-line complexity, @typescript-eslint/no-explicit-any
export const getQueryFilters = (filter: any): InteractionSummaryTypes.InteractionSummaryFilter => {
    let queryFilters: InteractionSummaryTypes.InteractionSummaryFilter = {};
    const rawFilter = toRaw(filter);
    let dateRange = null;

    if (rawFilter.date) {
        dateRange = rawFilter.date;
        if (isProxy(dateRange)) {
            dateRange = toRaw(dateRange);
        }
    }
    // Get timezone filter
    let rawTimezone = rawFilter.timezone || moment.tz.guess();
    if (isProxy(rawTimezone)) {
        rawTimezone = toRaw(rawTimezone);
    }
    const queryTimezone = rawTimezone ?? moment.tz.guess();

    // Get date filters
    if (dateRange && Array.isArray(dateRange) && (dateRange as []).length) {
    // As the date range is returned to us in UTC but uses the machine timezone when generating the start/end times, we need to adjust for that.
        queryFilters.fromDate = moment(dateRange[0]).tz(moment.tz.guess()).tz(queryTimezone, true).startOf('day').toISOString();
        queryFilters.toDate = moment(dateRange[1]).tz(moment.tz.guess()).tz(queryTimezone, true).endOf('day').toISOString();
    }
    // Get teams filter
    let rawTeams = rawFilter.teams?.value ?? rawFilter.teams;
    if (isProxy(rawTeams)) {
        rawTeams = toRaw(rawTeams);
    }
    // Get users filter
    let rawUsers = rawFilter.users?.value ?? rawFilter.users;
    if (isProxy(rawUsers)) {
        rawUsers = toRaw(rawUsers);
    }
    // Get workstreams filter
    let rawWorkstreams = rawFilter.workstreams?.value ?? rawFilter.workstreams;
    if (isProxy(rawWorkstreams)) {
        rawWorkstreams = toRaw(rawWorkstreams);
    }
    // Get roles filter
    let rawRoles = rawFilter.roles?.value ?? rawFilter.roles;
    if (isProxy(rawRoles)) {
        rawRoles = toRaw(rawRoles);
    }

    queryFilters = {
        ...queryFilters,
        ...(rawTeams && rawTeams.length && { teamIds: rawTeams }),
        ...(rawUsers && rawUsers.length && { userIds: rawUsers }),
        ...(rawWorkstreams && rawWorkstreams.length && { workstreams: rawWorkstreams }),
        ...(rawRoles && rawRoles.length && { roleIds: rawRoles }),
        ...(!!rawFilter.tags && { tags: rawFilter.tags }),
        ...(rawTimezone && { timezone: rawTimezone })
    };
    return queryFilters;
};
